import React from 'react';
import IDSContext from '@ids/context';
import ZIndex, { ZIndexManagerContextType } from '@ids-ts/zindex';
import { useTheme, Themeable } from '@design-systems/theme';
import Position, {
  RootRef,
  Alignments,
  Positions,
  PreventOverflow
} from '@ids-ts/position';
import ClickAwayListener from '@ids-ts/click-away-listener';
import clsx from 'clsx';
import isNil from 'lodash.isnil';
import logger from '@design-systems/logger';
import styles from './Menu.css';

import MenuList from './MenuList';
import { MenuItemProps } from './MenuItem';

export interface MenuProps extends Themeable {
  /** Key to match string */
  accessStringMatch?: string;
  /**
   * Alignment  to show the Menu, it is a string of alignments
   * vertical: 'top', 'bottom', 'middle'
   * horizontal: 'left', 'right', 'center'
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'left'
   */
  alignment?: Alignments;
  /** The element used to set the position of showing the Menu. */
  anchorElement: React.ReactElement;
  /**
   * If the anchor element is a React component, and it needs to an alternative reference, so setting the
   * "anchorElementRefPropName" in order to let popper can forward the ref to call. For example: if anchor
   * element is IDS TextField, then the "anchorElementRefPropName" would be "assignLabelRef" or "assignInputRef"
   * which is a prop function to get the text field DOM reference from the TextField.
   *
   * @default ''
   */
  anchorElementRefPropName?: string;
  /** aria-label */
  'aria-label'?: string;
  /**
   * Determine whether focus on a menu-item when Menu opens
   *
   * @default false
   */
  autoFocus?: boolean;
  /** Menu contents, normally are `MenuItem`s. */
  children?:
    | React.ReactElement<MenuItemProps>
    | React.ReactElement<MenuItemProps>[];
  /** Class to be assigned to Menu. Use to extend the style. */
  className?: string;
  /**
   * Determine whether Menu is disabled
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Determine whether MenuItem can be searched by typing
   *
   * @default false
   */
  disableSearch?: boolean;
  /** A callback function to get index of current selected item in Menu */
  getSelectedIndex?: (index: number) => void;
  /**
   * Determine whether the MenuItem is highlighted
   *
   * @default -1
   */
  highlightIndex?: number;
  /** ID to be applied to the menu. */
  id?: string;
  /**
   * Array of classnames.
   * If these classnames are clicked, the component is not to call onBlur.
   *
   * @default []
   */
  ignoreIdsOnBlur?: string[];
  /** Determine the maximum width of Menu, unit is px. */
  maxHeight?: string | number;
  /**
   * An ARIA role to be assigned to MenuItem.
   *
   * @default 'list'
   */
  menuItemRole?: string;
  /**
   * An ARIA role to be assigned to Menu.
   *
   * @default 'menu'
   */
  menuRole?: string;
  /** Determine the minimum width of Menu, unit is px. */
  minWidth?: string | number;
  /** Callback fired when the Menu wants to blur */
  onBlur?: (e: React.KeyboardEvent | React.FocusEvent) => void;
  /** Callback fired when the Menu wants to close */
  onClose?: (e: React.KeyboardEvent | React.FocusEvent) => void;
  /** Callback fired when the mouse hover the Menu */
  onMouseEnter?: React.MouseEventHandler;
  /** onSelect callback receive from parent to handle open/close, get selectedItem */
  onSelect?: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  /** Callback fired when the Menu gets clicked away */
  onClickAway?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  /** If `true`, the Menu shows. */
  open: boolean;
  /**
   * menuOffsetSkidding: the cross-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  menuOffsetSkidding?: number;
  /**
   * menuOffsetDistance: the main-axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   */
  menuOffsetDistance?: number;
  /**
   * menuOffsetX: the x axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   * @deprecated Please use menuOffsetSkidding instead
   */
  menuOffsetX?: number;
  /**
   * menuOffsetY: the y axis pixel distance that the popup should be offset from targetElement
   *
   * @default 0
   * @deprecated Please use menuOffsetDistance instead
   */
  menuOffsetY?: number;
  /**
   * Position to show the Menu, value is string of alignments
   * vertical: 'top', 'bottom'
   * horizontal: 'left', 'right'
   * NOTE: horizontal alignments cannot be paired with horizontal positions
   *
   * @default 'bottom'
   */
  position?: Positions;
  /**
   * preventMenuOverflow: an object variable; used to prevent Menu from being positioned outside the boundary.
   * More detail in @ids-ts/position preventOverflow API document.
   */
  preventMenuOverflow?: PreventOverflow;
  /** Callback that returns the ref for menu */
  reference?: (c: HTMLDivElement | null) => void;
  /**
   * This will ensure that the currently highlighted element will scroll
   * into the Menu view to always be visible if necessary
   *
   * @default false
   */
  scrollToSelected?: boolean;
  /**
   * Determine which search type to apply while typing
   * full - search sequentially adding typed symbols from the beginning
   * first - mimic all behavior to match first char only
   * partial - search for the first occurrence in the middle of displayed items
   *
   * @default 'first'
   */
  searchMode?: 'full' | 'first' | 'partial';
  /** This will highlight the selectedItem in Menu */
  selectedItem?: string | number | (string | number)[];
  /**
   * This will show a CheckMark on the current selected item in Menu
   *
   * @default false
   */
  showCheckmark?: boolean;
  /** The optional styles applied to the MenuList. */
  style?: React.CSSProperties;
  /** The optional styles applied to the position node wrapped around the root node. */
  stylePosition?: React.CSSProperties;
}

/**
 * IDS Menu Component
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const Menu = ({
  accessStringMatch,
  alignment = 'left',
  anchorElement,
  anchorElementRefPropName = '',
  'aria-label': ariaLabel,
  autoFocus = false,
  children,
  className,
  colorScheme,
  disabled = false,
  disableSearch = false,
  getSelectedIndex,
  id,
  ignoreIdsOnBlur = [],
  highlightIndex = -1,
  minWidth,
  maxHeight,
  menuItemRole = 'list',
  menuOffsetDistance = 0,
  menuOffsetSkidding = 0,
  menuOffsetX = 0,
  menuOffsetY = 0,
  menuRole = 'menu',
  onBlur,
  onClose,
  onMouseEnter,
  onSelect,
  onClickAway,
  open,
  position = 'bottom',
  preventMenuOverflow,
  reference,
  scrollToSelected = false,
  searchMode = 'first',
  selectedItem,
  showCheckmark = false,
  style,
  stylePosition,
  theme
}: MenuProps) => {
  const target = React.useRef<HTMLElement | null>(null);
  const setHasOpened = React.useState(false)[1];
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Warn users if they are still passing old props
  if (menuOffsetX) {
    logger.warn(
      'menuOffsetX prop is deprecated. Please use menuOffsetSkidding prop instead'
    );
  }

  if (menuOffsetY) {
    logger.warn(
      'menuOffsetY prop is deprecated. Please use menuOffsetDistance prop instead'
    );
  }

  React.useEffect(() => {
    open && setHasOpened(true);
  }, []);

  /** references the root element */
  const onRootRef = (
    ref: HTMLElement,
    refCallback: (elm: HTMLElement) => void
  ) => {
    target.current = ref;
    if (typeof refCallback === 'function') {
      refCallback(ref);
    }
  };

  /** function to get the menu dom element */
  const assignMenuRef = (setRefNode: (c: HTMLElement | null) => void) => {
    return (c: HTMLDivElement | null) => {
      menuRef.current = c;
      setRefNode && setRefNode(c);
      reference && reference(c);
    };
  };

  /** to close menu when use tab/Esc */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.persist();
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      onClose && onClose(event);
    }
  };

  /** if clicked anywhere other than the menu, close menu */
  const handleClickAway = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (onClickAway) {
      return onClickAway(event);
    }

    if (
      !event ||
      (menuRef.current && menuRef.current.contains(event.target as Node)) ||
      (target.current && target.current.contains(event.target as Node))
    ) {
      return;
    }

    if (open) {
      onClose && onClose(event as any);
      onBlur && onBlur(event as any);
    }
  };

  /** if open and current focus element is not inside the Menu, then close */
  const handleMenuBlur = (event: React.FocusEvent) => {
    // IE 11 identifies the current element via activeElement
    const activeElement = event.relatedTarget || document.activeElement;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: className does not exist on type EventTarget
    const { className: aeClassName, id: aeId } = activeElement;

    const shouldIgnoreBlur = [
      styles.menuItemWrapper,
      styles.menuItemContainer,
      styles.menuItemCheckmarkBox,
      styles.menuListWrapper
    ].reduce(
      (acc, cn) => acc || aeClassName.indexOf(cn) > -1,
      ignoreIdsOnBlur.reduce((acc, ignoreId) => acc || ignoreId === aeId, false)
    );

    if (open && !shouldIgnoreBlur) {
      onClose && onClose(event);
      onBlur && onBlur(event);
    }
  };

  /** Prevent the activeElement from being changed when click on a disabled mene-item */
  const handleDisabledItem =
    (child: React.ReactElement<MenuItemProps>) =>
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (child.props.onMouseDown) {
        child.props.onMouseDown(event);
      }
    };

  const { currentTheme, themeClass, currentColorScheme } = useTheme(
    { theme, colorScheme },
    styles
  );

  // items are MenuItems passed to Menu
  const items = React.Children.map(
    children as
      | React.ReactElement<MenuItemProps>
      | React.ReactElement<MenuItemProps>[],
    (child, index) => {
      const isSelected =
        (!isNil(selectedItem) && selectedItem === child.props.value) ||
        (Array.isArray(selectedItem) &&
          !isNil(child.props.value) &&
          selectedItem.includes(child.props.value)) ||
        child.props.selected;
      const isHighlighted = highlightIndex === index;
      if (isSelected) {
        getSelectedIndex && getSelectedIndex(index);
      }

      return React.cloneElement(child, {
        'aria-disabled': !!child.props.disabled,
        checkmark: showCheckmark,
        disabled: disabled || !!child.props.disabled,
        highlighted: isHighlighted,
        onMouseDown:
          disabled || child.props.disabled
            ? handleDisabledItem(child)
            : child.props.onMouseDown,
        onSelect: disabled || child.props.disabled ? undefined : onSelect,
        role: menuItemRole,
        selected: isSelected,
        theme: currentTheme,
        colorScheme: currentColorScheme
      });
    }
  );

  const positionElement = (
    <ClickAwayListener onClickAway={handleClickAway}>
      {(setRefNode, passthroughProps) => {
        const styleWithZIndex = passthroughProps?.style;

        const positionProps = {
          alignment,
          position,
          offsetSkidding: menuOffsetSkidding,
          offsetDistance: menuOffsetDistance,
          offsetX: menuOffsetX,
          offsetY: menuOffsetY,
          preventOverflow: preventMenuOverflow,
          show: open,
          targetElement: target.current as HTMLElement,
          id: id ? `idsMenu-position-${id}` : undefined,
          style: stylePosition || styleWithZIndex
        };

        return (
          <Position {...positionProps}>
            <MenuList
              aria-label={ariaLabel}
              autoFocus={autoFocus}
              className={clsx(className, themeClass)}
              disableSearch={disableSearch}
              searchMode={searchMode}
              id={id}
              minWidth={minWidth}
              maxHeight={maxHeight}
              menuRole={menuRole}
              reference={assignMenuRef(setRefNode)}
              scrollToSelected={scrollToSelected}
              style={style}
              theme={currentTheme}
              colorScheme={currentColorScheme}
              accessStringMatch={accessStringMatch}
              onKeyDown={handleKeyDown}
              onBlur={handleMenuBlur}
              onMouseEnter={onMouseEnter}
            >
              {items}
            </MenuList>
          </Position>
        );
      }}
    </ClickAwayListener>
  );

  const cloneAnchor = anchorElementRefPropName ? (
    React.cloneElement(anchorElement, {
      [anchorElementRefPropName]: (ref: HTMLElement) => {
        onRootRef(ref, anchorElement.props[anchorElementRefPropName]);
      }
    })
  ) : (
    <RootRef rootRef={onRootRef}>{anchorElement}</RootRef>
  );

  return (
    <>
      {cloneAnchor}
      {stylePosition ? (
        positionElement
      ) : (
        <IDSContext.Consumer>
          {(context: ZIndexManagerContextType) => (
            <ZIndex
              getCurrentBaseZIndex={context.getCurrentBaseZIndex}
              incrementBaseZIndex={context.incrementBaseZIndex}
              decrementBaseZIndex={context.decrementBaseZIndex}
            >
              {positionElement}
            </ZIndex>
          )}
        </IDSContext.Consumer>
      )}
    </>
  );
};

export default Menu;
